import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, BookOpen, ArrowUpRight } from "lucide-react";

const STORAGE_KEY = "tacc_last_preview";
const DISMISS_KEY = "tacc_last_preview_dismissed_at";
const SHOW_AFTER_MS = 6000;         // let visitors settle before nudging
const MAX_AGE_MS = 14 * 24 * 3600 * 1000; // 14 days
const DISMISS_COOLDOWN_MS = 24 * 3600 * 1000; // re-nudge at most once/day

function readBookmark() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const b = JSON.parse(raw);
    if (!b?.title || !b?.url || !b?.at) return null;
    if (Date.now() - b.at > MAX_AGE_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return b;
  } catch { return null; }
}

function wasRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < DISMISS_COOLDOWN_MS;
  } catch { return false; }
}

/**
 * Floating nudge for visitors who previewed a book/module last visit.
 * Appears only on repeat visits (bookmark saved on an EARLIER page-load, not this one),
 * after a delay, and never within 24h of being dismissed.
 */
export default function PreviewNudge() {
  const [bookmark, setBookmark] = useState(null);

  useEffect(() => {
    const b = readBookmark();
    if (!b) return;
    // Skip if the preview was just played this session — we only nudge on a *return* visit.
    const ageMinutes = (Date.now() - b.at) / 60000;
    if (ageMinutes < 5) return;
    if (wasRecentlyDismissed()) return;
    const t = setTimeout(() => setBookmark(b), SHOW_AFTER_MS);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (e) { console.warn("Storage unavailable:", e); }
    setBookmark(null);
  };

  const buy = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) { console.warn("Storage unavailable:", e); }
    setBookmark(null);
  };

  return (
    <AnimatePresence>
      {bookmark && (
        <motion.div
          key="preview-nudge"
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.95 }}
          transition={{ duration: 0.5, ease: [0.65, 0, 0.35, 1] }}
          className="fixed bottom-6 left-6 z-40 w-[min(360px,calc(100vw-3rem))] bg-[#FFFFFF] border border-[#F59E0B]/40 shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
          data-testid="preview-nudge"
        >
          <div className="p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#B45309]">
                <BookOpen size={12} />
                Picked up where you left off
              </div>
              <button
                onClick={dismiss}
                data-testid="preview-nudge-dismiss"
                aria-label="Dismiss"
                className="text-[#A8A29E] hover:text-[#1C1917] transition-colors -mt-0.5"
              >
                <X size={16} />
              </button>
            </div>
            <p className="font-display text-lg leading-tight tracking-tight text-[#1C1917] mb-2">
              You previewed <span className="italic text-[#B45309]">{bookmark.title}</span>.
            </p>
            <p className="text-xs text-[#78716C] leading-relaxed mb-4">
              Ready to keep reading? The full doctrine picks up where the preview stopped.
            </p>
            <a
              href={bookmark.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={buy}
              data-testid="preview-nudge-cta"
              className="btn-ember text-xs w-full justify-center"
            >
              Keep reading
              <ArrowUpRight size={14} />
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
